import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import ReactDOM from 'react-dom';
import { defineMessages, FormattedMessage, injectIntl } from 'react-intl';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { MutationInterop as Mutation } from '@confluence/apollo-relay-interop/entry-points/MutationInterop.ts';

// We have deprecated unstated. Please use react-sweet-state instead
// eslint-disable-next-line no-restricted-imports
import { Subscribe } from 'unstated';

import { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { SPACE_RESTRICTED_PAGES } from '@confluence/named-routes/entry-points/routes.ts';
// eslint-disable-next-line no-restricted-imports -- deprecated - use RouteState instead
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext.ts';

import { LoadableChangeEditionNotificationDialog } from '../NotificationDialog/index.js';

import { ChangeEditionClearRestrictionsForFreeMutation } from './ChangeEditionClearRestrictionsForFreeMutation.graphql.ts';

export const REMOVE_RESTRICTIONS_BUTTON_CLASS =
	'confluence-change-edition-remove-restrictions-button';
export const NOTIFICATION_DIALOG_PLACEHOLDER_ID = 'confluence-change-edition-notification-dialog';

const i18n = defineMessages({
	removeRestrictionButton: {
		id: 'change-edition.notification.dialog.general.button.remove-restrictions',
		defaultMessage: 'Remove restrictions',
		description: 'Remove restrictions button',
	},
	removeRestrictionTitle: {
		id: 'change-edition.notification.dialog.general.title.remove-restrictions-2',
		defaultMessage: 'Remove restrictions?',
		description: 'Remove restrictions title',
	},
	removeRestrictionTextSpaceLevel: {
		id: 'change-edition.notification.dialog.space.text.remove-restrictions',
		defaultMessage:
			'You’re on the free plan, so you can only <b>remove all restrictions</b> from <b>{contentTitle}</b>. <b>This can’t be undone.</b> You won’t be able to reset the restrictions later.',
		description: 'Remove restrictions title',
	},
	removeRestrictionConfirmationSuccessTitle: {
		id: 'change-edition.notification.dialog.confirmation.success.title',
		defaultMessage: 'Restrictions removed',
		description: 'Success confirmation title',
	},
	removeRestrictionConfirmationSuccessDesc: {
		id: 'change-edition.notification.dialog.confirmation.success.desc',
		defaultMessage: 'Anyone who’s logged in can now view and edit {contentTitle}.',
		description: 'Success confirmation description',
	},
	removeRestrictionConfirmationFailedTitle: {
		id: 'change-edition.notification.dialog.confirmation.failed.title',
		defaultMessage: 'Page restrictions not removed',
		description: 'Failed confirmation title',
	},
	removeRestrictionConfirmationFailedDesc: {
		id: 'change-edition.notification.dialog.confirmation.failed.desc',
		defaultMessage: 'We couldn’t remove restrictions from {contentTitle}. Try again.',
		description: 'Failed confirmation description',
	},
});

const DEFAULT_COMPONENT_STATE = {
	isNotificationOpen: false,
	isMutationInProcess: false,
	contentId: null,
	contentTitle: null,
};

//TODO: remove dep on shared i18n.
const changeEditionPageRestrictionsLegacyCoordinatorI18n = defineMessages({
	// Notification Dialog --------------------
	cancelButton: {
		id: 'change-edition.general.button.cancel',
		defaultMessage: 'Cancel',
		description: 'Cancel button',
	},
});

class ChangeEditionPageRestrictionsLegacyCoordinatorComponent extends PureComponent {
	static propTypes = {
		intl: PropTypes.object,
	};

	state = {
		...DEFAULT_COMPONENT_STATE,
	};

	componentDidMount() {
		Array.prototype.forEach.call(this.allRemoveRestrictionsElements, (el) => {
			el.addEventListener('click', this.openNotificationDialog);
		});
	}

	componentWillUnmount() {
		Array.prototype.forEach.call(this.allRemoveRestrictionsElements, (el) => {
			el.removeEventListener('click', this.openNotificationDialog);
		});
	}

	allRemoveRestrictionsElements =
		window.document.getElementsByClassName(REMOVE_RESTRICTIONS_BUTTON_CLASS) || [];

	placeholderEl = window.document.getElementById(NOTIFICATION_DIALOG_PLACEHOLDER_ID);

	openNotificationDialog = (event) => {
		this.setState({
			isNotificationOpen: true,
			contentId: event.target.getAttribute('data-page-id'),
			contentTitle: event.target.getAttribute('data-page-title'),
		});
	};

	closeNotificationDialog = () => {
		this.setState(DEFAULT_COMPONENT_STATE);
	};

	applyChanges = (mutationFunction) => () => {
		this.setState({
			isMutationInProcess: true,
		});
		mutationFunction();
	};

	handleMutationComplete = (flags) => () => {
		const { intl } = this.props;
		const { contentTitle } = this.state;

		flags.showSuccessFlag({
			title: intl.formatMessage(i18n.removeRestrictionConfirmationSuccessTitle),
			description: intl.formatMessage(i18n.removeRestrictionConfirmationSuccessDesc, {
				contentTitle,
			}),
		});
		this.closeNotificationDialog();

		window.setTimeout(() => {
			// refresh the page to see the changes
			window.location.reload();
		}, 500);
	};

	handleMutationError = (flags) => () => {
		const { intl } = this.props;
		const { contentTitle } = this.state;

		flags.showErrorFlag({
			title: intl.formatMessage(i18n.removeRestrictionConfirmationFailedTitle),
			description: intl.formatMessage(i18n.removeRestrictionConfirmationFailedDesc, {
				contentTitle,
			}),
		});
		this.closeNotificationDialog();
	};

	render() {
		const { intl } = this.props;
		const { isNotificationOpen, isMutationInProcess, contentTitle, contentId } = this.state;

		if (!this.placeholderEl) {
			return null;
		}

		return (
			<RoutesContext.Consumer>
				{({ location, matchRoute }) => {
					const route = matchRoute(location.pathname);

					// Skip rendering for other then "Space Setting / Restricted Page" page
					if (!route || route.name != SPACE_RESTRICTED_PAGES.name) {
						return null;
					}

					return ReactDOM.createPortal(
						<Subscribe to={[FlagsStateContainer]}>
							{(flags) => (
								<Mutation
									mutation={ChangeEditionClearRestrictionsForFreeMutation}
									variables={{ contentId }}
									onCompleted={this.handleMutationComplete(flags)}
									onError={this.handleMutationError(flags)}
								>
									{(clearRestrictionsForFreeMutation) => (
										<LoadableChangeEditionNotificationDialog
											isOpen={isNotificationOpen}
											isProcessing={isMutationInProcess}
											cancelAction={{
												text: intl.formatMessage(
													changeEditionPageRestrictionsLegacyCoordinatorI18n.cancelButton,
												),
												onClick: this.closeNotificationDialog,
											}}
											applyAction={{
												text: intl.formatMessage(i18n.removeRestrictionButton),
												onClick: this.applyChanges(clearRestrictionsForFreeMutation),
											}}
											heading={intl.formatMessage(i18n.removeRestrictionTitle)}
											source="individualSpaceRestrictedPages"
											grandfathered
										>
											<FormattedMessage
												{...i18n.removeRestrictionTextSpaceLevel}
												values={{
													contentTitle,
													b: (...chunks) => <b>{chunks}</b>,
												}}
											/>
										</LoadableChangeEditionNotificationDialog>
									)}
								</Mutation>
							)}
						</Subscribe>,
						this.placeholderEl,
					);
				}}
			</RoutesContext.Consumer>
		);
	}
}

export const ChangeEditionPageRestrictionsLegacyCoordinator = injectIntl(
	ChangeEditionPageRestrictionsLegacyCoordinatorComponent,
);
